import { motion, AnimatePresence } from 'framer-motion';

// Animation configs extracted to prevent re-renders from inline objects
const CARD_INITIAL = { opacity: 0, scale: 0.95 };
const CARD_ANIMATE = { opacity: 1, scale: 1 };
const CARD_TRANSITION = { delay: 0.2 };
const CHECK_INITIAL = { scale: 0 };
const CHECK_ANIMATE = { scale: [1, 1.5, 1] };
const CHECK_EXIT = { scale: 0 };
const BAR_INITIAL = { width: 0 };
const BAR_TRANSITION = { duration: 1, ease: "easeOut" };

const VoteSection = ({ topic, hasVoted, userVote, voteAnimation, onVote }) => {
  const totalVotes = (topic?.votes_for || 0) + (topic?.votes_against || 0);
  const forPercentage = totalVotes > 0 ? Math.round((topic?.votes_for / totalVotes) * 100) : 50;
  const againstPercentage = totalVotes > 0 ? Math.round((topic?.votes_against / totalVotes) * 100) : 50;

  const getForButtonClass = () => {
    if (userVote === 'for') return 'bg-vent-for text-black glow-green';
    if (hasVoted) return 'border-2 border-zinc-700 text-zinc-600 cursor-not-allowed';
    return 'btn-for';
  };

  const getAgainstButtonClass = () => {
    if (userVote === 'against') return 'bg-vent-against text-white glow-purple';
    if (hasVoted) return 'border-2 border-zinc-700 text-zinc-600 cursor-not-allowed';
    return 'btn-against';
  };

  return (
    <motion.div
      initial={CARD_INITIAL}
      animate={CARD_ANIMATE}
      transition={CARD_TRANSITION}
      className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-8 mb-6 sm:mb-8"
      data-testid="topic-card"
    >
      <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight" data-testid="topic-title">
        {topic?.title}
      </h2>
      <p className="text-zinc-400 text-base sm:text-lg mb-6 sm:mb-8" data-testid="topic-description">
        {topic?.description}
      </p>

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <motion.button
          whileHover={{ scale: hasVoted ? 1 : 1.02 }}
          whileTap={{ scale: hasVoted ? 1 : 0.98 }}
          onClick={() => onVote('for')}
          disabled={hasVoted}
          className={`flex-1 py-3 sm:py-4 px-6 sm:px-8 rounded-xl sm:rounded-2xl font-heading font-bold tracking-widest text-sm uppercase transition-all ${getForButtonClass()}`}
          data-testid="vote-for-btn"
        >
          <AnimatePresence>
            {voteAnimation === 'for' && (
              <motion.span initial={CHECK_INITIAL} animate={CHECK_ANIMATE} exit={CHECK_EXIT} className="inline-block mr-2">
                ✓
              </motion.span>
            )}
          </AnimatePresence>
          FOR
        </motion.button>

        <motion.button
          whileHover={{ scale: hasVoted ? 1 : 1.02 }}
          whileTap={{ scale: hasVoted ? 1 : 0.98 }}
          onClick={() => onVote('against')}
          disabled={hasVoted}
          className={`flex-1 py-3 sm:py-4 px-6 sm:px-8 rounded-xl sm:rounded-2xl font-heading font-bold tracking-widest text-sm uppercase transition-all ${getAgainstButtonClass()}`}
          data-testid="vote-against-btn"
        >
          <AnimatePresence>
            {voteAnimation === 'against' && (
              <motion.span initial={CHECK_INITIAL} animate={CHECK_ANIMATE} exit={CHECK_EXIT} className="inline-block mr-2">
                ✓
              </motion.span>
            )}
          </AnimatePresence>
          AGAINST
        </motion.button>
      </div>

      {hasVoted && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-6 pt-6 border-t border-white/10"
        >
          <div className="flex justify-between text-sm mb-2">
            <span className="text-vent-for">{forPercentage}% FOR</span>
            <span className="text-vent-against">{againstPercentage}% AGAINST</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden flex">
            <motion.div initial={BAR_INITIAL} animate={{ width: `${forPercentage}%` }} transition={BAR_TRANSITION} className="bg-vent-for" />
            <motion.div initial={BAR_INITIAL} animate={{ width: `${againstPercentage}%` }} transition={BAR_TRANSITION} className="bg-vent-against" />
          </div>
          <p className="text-center text-zinc-500 text-sm mt-2">{totalVotes} total votes</p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default VoteSection;
